import React, { createContext, useContext } from 'react';

import { getOrCreateGlobalContext } from '@adsk/alloy-react-context';

import {
  RemotePerformanceTrackingValue,
  TrackingRemoteReadyData,
  useTrackingPerformanceFactory,
} from '../hooks/usePerformanceTrackingFactory';
import { ReportRemoteValue, useReportFactory } from '../hooks/useReportFactory';

export interface RemoteContextValue
  extends RemotePerformanceTrackingValue, ReportRemoteValue {
  scope: string;
  moduleName: string;
}

const RemoteContextGlobal =
  getOrCreateGlobalContext<RemoteContextValue>('RemoteContext');

// Preserve non-optional useRemote() return type; runtime undefined matches
// pre-existing global-default behavior when no provider is mounted.
const RemoteContext = createContext<RemoteContextValue>(
  undefined as unknown as RemoteContextValue,
);

export const useRemote = (): RemoteContextValue => {
  const context = useContext(RemoteContext);
  const contextGlobal = useContext(RemoteContextGlobal);

  return context ?? contextGlobal;
};

export interface RemoteProviderProps {
  /** Child components that receive the remote context via `useRemote()`. */
  children: React.ReactNode;
  /** Module federation scope identifier for this remote. */
  scope: string;
  /** Federated module name (for example `"./App"`). */
  moduleName: string;
  /**
   * When `true`, skips the initial loading performance event because the remote
   * was already initialized before this provider mounted.
   */
  isRemoteAlreadyInitialized?: boolean;
}

/**
 * Supplies remote context (scope, module name, performance tracking, and reporting)
 * through a global React context shared across the application.
 *
 * Use at the root of a remote entry or when testing components that call `useRemote()`.
 * For a scoped local context (for example Module Federation bridge hosts), use
 * {@link RemoteBridgeProvider} instead.
 */
export const RemoteProvider: React.FC<RemoteProviderProps> = ({
  children,
  scope,
  moduleName,
  isRemoteAlreadyInitialized,
}) => {
  const { trackLoading, trackReady } = useTrackingPerformanceFactory(
    scope,
    moduleName,
    isRemoteAlreadyInitialized,
  );

  const { report } = useReportFactory({ scope });

  return (
    <RemoteContextGlobal.Provider
      value={{ scope, moduleName, trackLoading, trackReady, report }}
    >
      {children}
    </RemoteContextGlobal.Provider>
  );
};

/**
 * Supplies the same remote context as {@link RemoteProvider}, but through a local
 * React context instead of the global one.
 *
 * `useRemote()` prefers this context over the global provider, which is useful when
 * a bridge host needs an isolated remote identity without overriding app-wide context.
 */
export const RemoteBridgeProvider: React.FC<RemoteProviderProps> = ({
  children,
  scope,
  moduleName,
  isRemoteAlreadyInitialized,
}) => {
  const { trackLoading, trackReady } = useTrackingPerformanceFactory(
    scope,
    moduleName,
    isRemoteAlreadyInitialized,
  );

  const { report } = useReportFactory({ scope });

  return (
    <RemoteContext.Provider
      value={{ scope, moduleName, trackLoading, trackReady, report }}
    >
      {children}
    </RemoteContext.Provider>
  );
};

////////////// Deprecated

/**
 * @deprecated
 */
const RemotePerformanceTrackingContext = getOrCreateGlobalContext<{
  reportRemoteLoading: RemotePerformanceTrackingValue['trackLoading'];
  reportRemoteReady: RemotePerformanceTrackingValue['trackReady'];
}>('RemotePerformanceTrackingContext');

/**
 * @deprecated - use useRemote() instead
 */
export const useRemotePerformanceTracking = () => {
  const { trackLoading: reportRemoteLoading, trackReady: reportRemoteReady } =
    useRemote();
  return {
    reportRemoteLoading,
    reportRemoteReady,
  };
};

const RemotePerformanceTrackingProviderInitializer: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const { reportRemoteLoading, reportRemoteReady } =
    useRemotePerformanceTracking();

  return (
    <RemotePerformanceTrackingContext.Provider
      value={{ reportRemoteLoading, reportRemoteReady }}
    >
      {children}
    </RemotePerformanceTrackingContext.Provider>
  );
};

/**
 * @deprecated
 */
export const RemotePerformanceTrackingProvider: React.FC<
  RemoteProviderProps
> = ({ children, scope, moduleName, isRemoteAlreadyInitialized }) => {
  return (
    <RemoteProvider
      scope={scope}
      moduleName={moduleName}
      isRemoteAlreadyInitialized={isRemoteAlreadyInitialized}
    >
      <RemotePerformanceTrackingProviderInitializer>
        {children}
      </RemotePerformanceTrackingProviderInitializer>
    </RemoteProvider>
  );
};

/**
 * @deprecated
 */
export type RemotePerformanceTrackingContextValue = {
  reportRemoteLoading: () => void;
  reportRemoteReady: (data?: TrackingRemoteReadyData) => void;
};
